import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { 
  HardDrive, 
  FolderOpen, 
  Image, 
  FileText, 
  Search,
  RefreshCw,
  ChevronRight,
  TrendingUp,
  AlertTriangle,
  Download,
  Trash2,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Storage quota tiers (for future paid plans)
const STORAGE_TIERS = {
  free: { limit: 1024 * 1024 * 1024, name: 'Gratuito', color: 'bg-slate-500' }, // 1GB
  starter: { limit: 5 * 1024 * 1024 * 1024, name: 'Starter', color: 'bg-blue-500' }, // 5GB
  pro: { limit: 25 * 1024 * 1024 * 1024, name: 'Pro', color: 'bg-purple-500' }, // 25GB
  enterprise: { limit: 100 * 1024 * 1024 * 1024, name: 'Enterprise', color: 'bg-orange-500' } // 100GB
};

function formatBytes(bytes, decimals = 2) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

function StorageUsageCard({ title, files, sizeBytes, sizeFormatted, icon: Icon, color }) {
  return (
    <Card className="bg-slate-800/50 border-slate-700">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-400">{title}</p>
            <p className="text-2xl font-bold text-white mt-1">{sizeFormatted}</p>
            <p className="text-xs text-slate-500 mt-1">{files} arquivos</p>
          </div>
          <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function TripStorageRow({ trip, onViewDetails }) {
  const usagePercent = (trip.total_size_bytes / STORAGE_TIERS.free.limit) * 100;
  const isNearLimit = usagePercent > 80;
  
  return (
    <tr className="border-b border-slate-700/50 hover:bg-slate-800/50 transition-colors">
      <td className="px-4 py-3">
        <div>
          <p className="font-medium text-white">{trip.trip_name}</p>
          <p className="text-xs text-slate-400">Código: {trip.trip_code}</p>
        </div>
      </td>
      <td className="px-4 py-3">
        <Badge variant="outline" className="border-slate-600 text-slate-300">
          {trip.total_files} arquivos
        </Badge>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          {trip.by_folder?.capa && (
            <Badge variant="outline" className="border-orange-500/50 text-orange-400 text-xs">
              {trip.by_folder.capa} capa
            </Badge>
          )}
          {trip.by_folder?.fotos && (
            <Badge variant="outline" className="border-blue-500/50 text-blue-400 text-xs">
              {trip.by_folder.fotos} fotos
            </Badge>
          )}
          {trip.by_folder?.arquivos && (
            <Badge variant="outline" className="border-green-500/50 text-green-400 text-xs">
              {trip.by_folder.arquivos} arquivos
            </Badge>
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <span className={`font-medium ${isNearLimit ? 'text-red-400' : 'text-white'}`}>
            {trip.total_size_formatted}
          </span>
          {isNearLimit && <AlertTriangle className="w-4 h-4 text-red-400" />}
        </div>
      </td>
      <td className="px-4 py-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onViewDetails(trip)}
          className="text-slate-400 hover:text-white"
        >
          <Eye className="w-4 h-4 mr-1" />
          Detalhes
        </Button>
      </td>
    </tr>
  );
}

function TripDetailModal({ trip, onClose }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const res = await fetch(`${API_URL}/api/storage/admin/trips/${trip.trip_id}/usage`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setDetails(data);
        }
      } catch (error) {
        toast.error('Erro ao carregar detalhes');
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [trip.trip_id, token]);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <Card className="bg-slate-800 border-slate-700 w-full max-w-2xl max-h-[80vh] overflow-auto">
          <CardContent className="p-6 flex items-center justify-center">
            <RefreshCw className="w-6 h-6 animate-spin text-orange-500" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="bg-slate-800 border-slate-700 w-full max-w-3xl max-h-[80vh] overflow-auto">
        <CardHeader className="border-b border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-white">{details?.trip_name}</CardTitle>
              <CardDescription>Código: {details?.trip_code}</CardDescription>
            </div>
            <Button variant="ghost" onClick={onClose} className="text-slate-400">
              ✕
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          {/* Summary */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-slate-700/50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-white">{details?.total_files || 0}</p>
              <p className="text-sm text-slate-400">Arquivos</p>
            </div>
            <div className="bg-slate-700/50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-orange-400">{details?.total_size_formatted}</p>
              <p className="text-sm text-slate-400">Tamanho Total</p>
            </div>
            <div className="bg-slate-700/50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-blue-400">{details?.by_user?.length || 0}</p>
              <p className="text-sm text-slate-400">Usuários</p>
            </div>
          </div>

          {/* By Folder */}
          {details?.by_folder && Object.keys(details.by_folder).length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-slate-300 mb-3">Por Pasta</h4>
              <div className="space-y-2">
                {Object.entries(details.by_folder).map(([folder, data]) => (
                  <div key={folder} className="flex items-center justify-between bg-slate-700/30 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <FolderOpen className="w-4 h-4 text-orange-400" />
                      <span className="text-white capitalize">{folder}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-white font-medium">{data.size_formatted}</span>
                      <span className="text-slate-400 text-sm ml-2">({data.files} arquivos)</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* By User */}
          {details?.by_user && details.by_user.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-slate-300 mb-3">Por Usuário</h4>
              <div className="space-y-2">
                {details.by_user.map((user, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-slate-700/30 rounded-lg p-3">
                    <div>
                      <p className="text-white">{user.name}</p>
                      <p className="text-xs text-slate-400">{user.email}</p>
                    </div>
                    <div className="text-right">
                      <span className="text-white font-medium">{user.size_formatted}</span>
                      <span className="text-slate-400 text-sm ml-2">({user.files} arquivos)</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Largest Files */}
          {details?.largest_files && details.largest_files.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-slate-300 mb-3">Maiores Arquivos</h4>
              <div className="space-y-2">
                {details.largest_files.slice(0, 5).map((file, idx) => (
                  <div key={file.id} className="flex items-center justify-between bg-slate-700/30 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-slate-400" />
                      <span className="text-white truncate max-w-[200px]">{file.filename}</span>
                      <Badge variant="outline" className="text-xs border-slate-600">
                        {file.folder}
                      </Badge>
                    </div>
                    <span className="text-orange-400 font-medium">{file.size_formatted}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function AdminStorage() {
  const [storageData, setStorageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTrip, setSelectedTrip] = useState(null);
  const { token } = useAuth();

  const fetchStorageUsage = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/storage/admin/usage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStorageData(data);
      } else if (res.status === 503) {
        toast.error('Serviço de storage não configurado');
      } else {
        toast.error('Erro ao carregar dados de storage');
      }
    } catch (error) {
      console.error('Error fetching storage:', error);
      toast.error('Erro de conexão');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStorageUsage();
  }, [token]);

  const filteredTrips = storageData?.trips?.filter(trip =>
    trip.trip_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    trip.trip_code.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  // Calculate usage percentage (based on free tier)
  const usagePercent = storageData ? (storageData.total_size_bytes / STORAGE_TIERS.free.limit) * 100 : 0;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Armazenamento</h1>
        </div>
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Armazenamento</h1>
          <p className="text-slate-400">Monitoramento de uso do Cloudflare R2</p>
        </div>
        <Button onClick={fetchStorageUsage} variant="outline" className="border-slate-600">
          <RefreshCw className="w-4 h-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StorageUsageCard
          title="Armazenamento Total"
          files={storageData?.total_files || 0}
          sizeBytes={storageData?.total_size_bytes || 0}
          sizeFormatted={storageData?.total_size_formatted || '0 B'}
          icon={HardDrive}
          color="bg-gradient-to-br from-orange-500 to-red-500"
        />
        <StorageUsageCard
          title="Viagens"
          files={storageData?.trips?.reduce((acc, t) => acc + t.total_files, 0) || 0}
          sizeBytes={storageData?.trips?.reduce((acc, t) => acc + t.total_size_bytes, 0) || 0}
          sizeFormatted={formatBytes(storageData?.trips?.reduce((acc, t) => acc + t.total_size_bytes, 0) || 0)}
          icon={FolderOpen}
          color="bg-gradient-to-br from-blue-500 to-purple-500"
        />
        <StorageUsageCard
          title="Admin"
          files={storageData?.admin_files || 0}
          sizeBytes={storageData?.admin_size_bytes || 0}
          sizeFormatted={storageData?.admin_size_formatted || '0 B'}
          icon={FileText}
          color="bg-gradient-to-br from-green-500 to-teal-500"
        />
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-slate-400">Uso do Plano</p>
              <Badge className={STORAGE_TIERS.free.color}>{STORAGE_TIERS.free.name}</Badge>
            </div>
            <Progress value={Math.min(usagePercent, 100)} className="h-2 mb-2" />
            <div className="flex justify-between text-xs">
              <span className="text-slate-400">{storageData?.total_size_formatted}</span>
              <span className="text-slate-400">{formatBytes(STORAGE_TIERS.free.limit)}</span>
            </div>
            {usagePercent > 80 && (
              <div className="mt-3 flex items-center gap-2 text-yellow-400 text-xs">
                <AlertTriangle className="w-4 h-4" />
                Próximo do limite
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Trips Table */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-white">Uso por Viagem</CardTitle>
              <CardDescription>
                {storageData?.trips?.length || 0} viagens com arquivos
              </CardDescription>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar viagem..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-slate-700/50 border-slate-600 text-white w-64"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredTrips.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700 text-left">
                    <th className="px-4 py-3 text-xs font-medium text-slate-400 uppercase">Viagem</th>
                    <th className="px-4 py-3 text-xs font-medium text-slate-400 uppercase">Arquivos</th>
                    <th className="px-4 py-3 text-xs font-medium text-slate-400 uppercase">Pastas</th>
                    <th className="px-4 py-3 text-xs font-medium text-slate-400 uppercase">Tamanho</th>
                    <th className="px-4 py-3 text-xs font-medium text-slate-400 uppercase">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTrips.map((trip) => (
                    <TripStorageRow 
                      key={trip.trip_id} 
                      trip={trip}
                      onViewDetails={setSelectedTrip}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <HardDrive className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">
                {searchQuery ? 'Nenhuma viagem encontrada' : 'Nenhum arquivo armazenado ainda'}
              </p>
              <p className="text-sm text-slate-500 mt-1">
                Os arquivos aparecerão aqui quando os usuários fizerem uploads
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Future Plans Info */}
      <Card className="bg-gradient-to-br from-slate-800/50 to-orange-900/20 border-orange-500/20">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-orange-500/20 flex items-center justify-center flex-shrink-0">
              <TrendingUp className="w-6 h-6 text-orange-400" />
            </div>
            <div>
              <h3 className="font-semibold text-white mb-2">Planos de Armazenamento</h3>
              <p className="text-sm text-slate-300 mb-4">
                Monitore o uso de armazenamento para implementar planos pagos baseados em consumo.
                Atualmente todos os usuários estão no plano gratuito com limite de 1GB por viagem.
              </p>
              <div className="flex gap-4">
                {Object.entries(STORAGE_TIERS).map(([key, tier]) => (
                  <div key={key} className="text-center">
                    <Badge className={`${tier.color} mb-1`}>{tier.name}</Badge>
                    <p className="text-xs text-slate-400">{formatBytes(tier.limit)}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Detail Modal */}
      {selectedTrip && (
        <TripDetailModal 
          trip={selectedTrip} 
          onClose={() => setSelectedTrip(null)} 
        />
      )}
    </div>
  );
}
